<template>
  <el-dialog
    v-model="dialogVisible"
    :title="dialogTitle"
    width="560px"
    append-to-body
    @closed="onClosed"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
      v-loading="submitting"
    >
      <!-- Entity toggle (create only; edit is locked to the row's entity) -->
      <el-form-item v-if="isCreate" label="Tur">
        <el-radio-group v-model="entity" @change="onEntityChange">
          <el-radio-button label="region">Viloyat</el-radio-button>
          <el-radio-button label="district">Tuman</el-radio-button>
        </el-radio-group>
      </el-form-item>

      <!-- Pickers (create) -->
      <template v-if="isCreate">
        <el-form-item label="Viloyat" prop="region_id" :error="serverErrors.region_id">
          <el-select
            v-model="form.region_id"
            style="width: 100%"
            placeholder="Viloyatni tanlang"
            filterable
            :loading="regionsLoading"
            @change="onRegionChange"
          >
            <el-option
              v-for="region in regions"
              :key="region.id"
              :value="region.id"
              :label="region.title"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="entity === 'district'"
          label="Tuman"
          prop="district_id"
          :error="serverErrors.district_id"
        >
          <el-select
            v-model="form.district_id"
            style="width: 100%"
            placeholder="Tumanni tanlang"
            filterable
            :loading="districtsLoading"
            :disabled="!form.region_id"
          >
            <el-option
              v-for="district in districts"
              :key="district.id"
              :value="district.id"
              :label="district.title"
            />
          </el-select>
        </el-form-item>
      </template>

      <!-- Locked context (edit) -->
      <template v-else>
        <el-form-item label="Hudud">
          <el-input :model-value="editContextTitle" disabled />
        </el-form-item>
      </template>

      <el-row :gutter="12">
        <el-col :span="12">
          <el-form-item label="Yil" prop="year" :error="serverErrors.year">
            <el-select
              v-model="form.year"
              style="width: 100%"
              placeholder="Yil"
              :disabled="!isCreate"
            >
              <el-option v-for="y in years" :key="y" :value="y" :label="y" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="Chorak" prop="quarter" :error="serverErrors.quarter">
            <el-radio-group v-model="form.quarter" :disabled="!isCreate">
              <el-radio-button v-for="q in 4" :key="q" :label="q">
                {{ q }}-chorak
              </el-radio-button>
            </el-radio-group>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="Kirim (so'm)" prop="income" :error="serverErrors.income">
        <mf-price-input v-model="form.income" style="width: 100%" @update:model-value="clearServerError('income')" />
      </el-form-item>
      <el-form-item label="Chiqim (so'm)" prop="outcome" :error="serverErrors.outcome">
        <mf-price-input v-model="form.outcome" style="width: 100%" @update:model-value="clearServerError('outcome')" />
      </el-form-item>
      <el-form-item label="Transfer (so'm)" prop="transfer" :error="serverErrors.transfer">
        <mf-price-input v-model="form.transfer" style="width: 100%" @update:model-value="clearServerError('transfer')" />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="dialogVisible = false">Bekor qilish</el-button>
      <el-button type="primary" :loading="submitting" @click="onSubmit">
        Saqlash
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import { formatterPrice } from "@/utils/common";
import MfPriceInput from "@/components/core/MfPriceInput.vue";
import api from "./financeReportsApi";

const amountValidator = (rule, value, callback) => {
  // 0 and empty are valid (empty is coerced to 0 on submit). Only a negative
  // or non-numeric value is rejected. In practice MfPriceInput already strips
  // any non-digit input, so this is just a safety net.
  if (value === null || value === undefined || value === "") {
    return callback();
  }
  const num = Number(value);
  if (Number.isNaN(num) || num < 0) {
    return callback(new Error("Faqat manfiy bo'lmagan son kiriting"));
  }
  callback();
};

export default {
  name: "FinanceReportFormModal",
  components: { MfPriceInput },
  props: {
    modelValue: { type: Boolean, default: false },
    mode: { type: String, default: "create" }, // "create" | "edit"
    row: { type: Object, default: null }, // edit target, or existing row context
    defaults: { type: Object, default: () => ({}) }, // { entity, year, quarter }
  },
  emits: ["update:modelValue", "saved"],
  data() {
    return {
      entity: "region",
      regions: [],
      districts: [],
      regionsLoading: false,
      districtsLoading: false,
      submitting: false,
      serverErrors: {},
      form: {
        region_id: null,
        district_id: null,
        year: null,
        quarter: null,
        income: null,
        outcome: null,
        transfer: null,
      },
      rules: {
        region_id: [{ required: true, message: "Majburiy maydon", trigger: "change" }],
        district_id: [{ required: true, message: "Majburiy maydon", trigger: "change" }],
        year: [{ required: true, message: "Majburiy maydon", trigger: "change" }],
        quarter: [{ required: true, message: "Majburiy maydon", trigger: "change" }],
        income: [{ validator: amountValidator, trigger: "change" }],
        outcome: [{ validator: amountValidator, trigger: "change" }],
        transfer: [{ validator: amountValidator, trigger: "change" }],
      },
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    isCreate() {
      return this.mode !== "edit";
    },
    dialogTitle() {
      return this.isCreate ? "Yangi ma'lumot kiritish" : "Ma'lumotni tahrirlash";
    },
    years() {
      const current = new Date().getFullYear();
      const list = [];
      for (let y = current + 1; y >= 2020; y--) list.push(y);
      return list;
    },
    editContextTitle() {
      if (!this.row) return "";
      const period = `${this.row.year}-yil, ${this.row.quarter}-chorak`;
      if (this.entity === "district") {
        return `${this.row.region_title} / ${this.row.district_title} — ${period}`;
      }
      return `${this.row.region_title} — ${period}`;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.init();
    },
  },
  methods: {
    init() {
      this.serverErrors = {};
      if (this.isCreate) {
        this.entity = this.defaults.entity || "region";
        this.form = {
          region_id: null,
          district_id: null,
          year: this.defaults.year || new Date().getFullYear(),
          quarter: this.defaults.quarter || 1,
          income: null,
          outcome: null,
          transfer: null,
        };
        this.districts = [];
        this.loadRegions();
      } else if (this.row) {
        this.entity = this.row.entity;
        this.form = {
          region_id: this.row.region_id ?? null,
          district_id: this.row.district_id ?? null,
          year: this.row.year,
          quarter: this.row.quarter,
          income: this.row.income,
          outcome: this.row.outcome,
          transfer: this.row.transfer,
        };
      }
      this.$nextTick(() => this.$refs.formRef?.clearValidate());
    },
    onClosed() {
      this.$refs.formRef?.clearValidate();
      this.serverErrors = {};
    },
    onEntityChange() {
      this.form.region_id = null;
      this.form.district_id = null;
      this.districts = [];
      this.$nextTick(() => this.$refs.formRef?.clearValidate());
    },
    onRegionChange() {
      this.form.district_id = null;
      this.districts = [];
      this.serverErrors = { ...this.serverErrors, region_id: undefined };
      if (this.entity === "district") this.loadDistricts();
    },
    clearServerError(field) {
      if (this.serverErrors[field]) {
        this.serverErrors = { ...this.serverErrors, [field]: undefined };
      }
    },
    loadRegions() {
      this.regionsLoading = true;
      api
        .listRegionPicker()
        .then(({ data }) => {
          this.regions = data.regions || [];
        })
        .catch(() => ElMessage.error("Viloyatlarni yuklashda xatolik"))
        .finally(() => {
          this.regionsLoading = false;
        });
    },
    loadDistricts() {
      if (!this.form.region_id) return;
      this.districtsLoading = true;
      api
        .listDistrictPicker(this.form.region_id)
        .then(({ data }) => {
          this.districts = data.districts || [];
        })
        .catch(() => ElMessage.error("Tumanlarni yuklashda xatolik"))
        .finally(() => {
          this.districtsLoading = false;
        });
    },
    onSubmit() {
      this.$refs.formRef.validate((valid) => {
        if (!valid) return;
        if (this.isCreate) {
          this.checkExistingThenCreate();
        } else {
          this.submitEdit();
        }
      });
    },
    // §6.3 — warn before overwriting an existing natural key (the backend
    // versioned-upsert will overwrite regardless; this only prevents mistakes).
    checkExistingThenCreate() {
      this.submitting = true;
      const { year, quarter } = this.form;
      const lookup =
        this.entity === "region"
          ? api.listRegions({ year, quarter })
          : api.listDistricts({ year, quarter, region_id: this.form.region_id });

      lookup
        .then(({ data }) => {
          const rows =
            this.entity === "region"
              ? data.region_finance_reports || []
              : data.district_finance_reports || [];
          const existing =
            this.entity === "region"
              ? rows.find((r) => r.region_id === this.form.region_id)
              : rows.find((r) => r.district_id === this.form.district_id);
          if (existing) return this.confirmOverwrite(existing);
          return true;
        })
        .then((proceed) => {
          if (proceed) this.submitCreate();
          else this.submitting = false;
        })
        .catch((err) => {
          // Cancelling the overwrite dialog rejects — treat as a silent abort.
          if (err === "cancel" || err === "close") {
            this.submitting = false;
            return;
          }
          this.submitting = false;
          ElNotification({
            title: "Xatolik",
            message: "Mavjud ma'lumotni tekshirishda xatolik. Qayta urinib ko'ring.",
            type: "error",
          });
        });
    },
    confirmOverwrite(existing) {
      const msg =
        `Bu davr uchun ma'lumot allaqachon mavjud ` +
        `(Kirim: ${formatterPrice(existing.income)}, ` +
        `Chiqim: ${formatterPrice(existing.outcome)}, ` +
        `Transfer: ${formatterPrice(existing.transfer)}). ` +
        `Yangi qiymatlar bilan almashtirilsinmi?`;
      return ElMessageBox.confirm(msg, "Diqqat!", {
        confirmButtonText: "Ha, almashtirilsin",
        cancelButtonText: "Bekor qilish",
        type: "warning",
      }).then(() => true);
    },
    submitCreate() {
      this.submitting = true;
      this.serverErrors = {};
      const base = {
        year: this.form.year,
        quarter: this.form.quarter,
        income: this.form.income ?? 0,
        outcome: this.form.outcome ?? 0,
        transfer: this.form.transfer ?? 0,
      };
      const request =
        this.entity === "region"
          ? api.createRegion({ region_id: this.form.region_id, ...base })
          : // §4.3 — do NOT send region_id for districts; backend resolves it.
            api.createDistrict({ district_id: this.form.district_id, ...base });

      request
        .then(() => {
          ElNotification({
            title: "Tabriklaymiz",
            message: "Ma'lumot muvaffaqiyatli saqlandi",
            type: "success",
          });
          this.$emit("saved");
          this.dialogVisible = false;
        })
        .catch(({ response }) => this.applyServerErrors(response))
        .finally(() => {
          this.submitting = false;
        });
    },
    submitEdit() {
      this.submitting = true;
      this.serverErrors = {};
      const body = {
        income: this.form.income ?? 0,
        outcome: this.form.outcome ?? 0,
        transfer: this.form.transfer ?? 0,
      };
      const request =
        this.entity === "region"
          ? api.updateRegion(this.row.id, body)
          : api.updateDistrict(this.row.id, body);

      request
        .then(() => {
          ElNotification({
            title: "Tabriklaymiz",
            message: "Ma'lumot yangilandi",
            type: "success",
          });
          // §3.4 — PUT returns 200 even for a stale/deleted id; parent refetches
          // to confirm the change actually landed.
          this.$emit("saved");
          this.dialogVisible = false;
        })
        .catch(({ response }) => this.applyServerErrors(response))
        .finally(() => {
          this.submitting = false;
        });
    },
    applyServerErrors(response) {
      const data = response?.data;
      const args = data?.invalid_args;
      if (Array.isArray(args) && args.length) {
        const next = {};
        args.forEach(({ field, tag }) => {
          next[field] = this.tagMessage(tag);
        });
        this.serverErrors = next;
      }
      ElNotification({
        title: "Xatolik",
        message: data?.message || "Saqlashda xatolik yuz berdi",
        type: "error",
      });
    },
    tagMessage(tag) {
      const map = {
        required: "Majburiy maydon",
        min: "Qiymat juda kichik",
        max: "Qiymat juda katta",
      };
      return map[tag] || "Noto'g'ri qiymat";
    },
  },
};
</script>

<style scoped></style>
